import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, BarChart3, Users, Settings as SettingsIcon, Loader2, AlertCircle } from 'lucide-react';
import { useWebTrackingData } from '../hooks/useWebTrackingData';
import { useRBAC } from '../contexts/RBACContext';
import { PERMISSIONS } from '../config/rbac';
import WebTrackingOverview from '../components/web-tracking/WebTrackingOverview';
import WebTrackingVisitors from '../components/web-tracking/WebTrackingVisitors';
import WebTrackingVisitorDetail from '../components/web-tracking/WebTrackingVisitorDetail';
import TrackedSiteSetup from '../components/web-tracking/TrackedSiteSetup';
import TrackedSiteUserManagement from '../components/web-tracking/TrackedSiteUserManagement';

const TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'visitors', label: 'Visitors', icon: Users },
  { key: 'sites', label: 'Sites', icon: SettingsIcon },
];

const WebTracking = () => {
  const { hasPermission } = useRBAC();
  const canManage = hasPermission(PERMISSIONS.MANAGE_WEB_TRACKING);

  const [activeTab, setActiveTab] = useState('overview');
  const [managingUsersForSiteId, setManagingUsersForSiteId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const {
    sites,
    selectedSite,
    setSelectedSite,
    analytics,
    visitors,
    visitorHistory,
    selectedVisitor,
    setSelectedVisitor,
    loading,
    analyticsLoading,
    visitorsLoading,
    historyLoading,
    error,
    fetchAnalytics,
    fetchVisitors,
    fetchVisitorHistory,
    addSite,
    updateSite,
    removeSite,
    toggleSiteActive,
    addSiteUser,
    removeSiteUser,
    fetchSiteUsers,
    deleteVisitorData,
  } = useWebTrackingData();

  // ─── Handlers ───
  const handleAddSite = useCallback(async (data) => {
    setActionError(null);
    try {
      await addSite(data);
    } catch (err) {
      setActionError(err.message);
      throw err;
    }
  }, [addSite]);

  const handleToggleActive = useCallback(async (siteId) => {
    setActionError(null);
    try {
      await toggleSiteActive(siteId);
    } catch (err) {
      setActionError(err.message);
    }
  }, [toggleSiteActive]);

  const handleRemoveSite = useCallback(async (siteId) => {
    setActionError(null);
    try {
      await removeSite(siteId);
    } catch (err) {
      setActionError(err.message);
    }
  }, [removeSite]);

  const handleSelectVisitor = useCallback((leadEmail) => {
    fetchVisitorHistory(leadEmail);
  }, [fetchVisitorHistory]);

  const handleBackFromDetail = useCallback(() => {
    setSelectedVisitor(null);
  }, [setSelectedVisitor]);

  const handleDeleteVisitorData = useCallback(async (email) => {
    setActionError(null);
    try {
      await deleteVisitorData(email);
      setSelectedVisitor(null);
      // Refresh visitors
      fetchVisitors({ page: 1, pageSize: 20, sortBy: 'last_visit', sortOrder: 'desc' });
    } catch (err) {
      setActionError(err.message);
    }
  }, [deleteVisitorData, setSelectedVisitor, fetchVisitors]);

  const handleRangeChange = useCallback((days) => {
    fetchAnalytics(days);
  }, [fetchAnalytics]);

  // ─── Loading ───
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-3 text-gray-600">Loading Web Tracking...</span>
      </div>
    );
  }

  // ─── No sites → show setup ───
  if (sites.length === 0) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Globe className="w-7 h-7 text-primary-600" />
            Web Tracking
          </h1>
          <p className="text-gray-500 mt-1">Track website visitors and connect them to your email leads</p>
        </div>
        <TrackedSiteSetup
          sites={[]}
          onAddSite={handleAddSite}
          onToggleActive={handleToggleActive}
          onRemoveSite={handleRemoveSite}
          onManageUsers={setManagingUsersForSiteId}
          canManage={canManage}
        />
      </div>
    );
  }

  // If viewing a specific visitor detail
  if (selectedVisitor) {
    return (
      <div className="p-6">
        <WebTrackingVisitorDetail
          visitorEmail={selectedVisitor}
          history={visitorHistory}
          loading={historyLoading}
          onBack={handleBackFromDetail}
          onDeleteData={canManage ? handleDeleteVisitorData : null}
          canManage={canManage}
        />
      </div>
    );
  }

  // ─── Site selector (if multiple) ───
  const managedSite = sites.find(s => s.id === managingUsersForSiteId);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Globe className="w-7 h-7 text-primary-600" />
            Web Tracking
          </h1>
          <p className="text-gray-500 mt-1">Track website visitors and connect them to your email leads</p>
        </div>

        {/* Site selector */}
        {sites.length > 1 && (
          <select
            value={selectedSite?.id || ''}
            onChange={(e) => {
              const site = sites.find(s => s.id === e.target.value);
              if (site) setSelectedSite(site);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {sites.map(s => (
              <option key={s.id} value={s.id}>
                {s.site_name} ({s.domain})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Error banner */}
      {(error || actionError) && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {actionError || error}
        </div>
      )}

      {/* Tab Bar */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.15 }}
        >
          {activeTab === 'overview' && (
            <WebTrackingOverview
              analytics={analytics}
              loading={analyticsLoading}
              onRangeChange={handleRangeChange}
            />
          )}

          {activeTab === 'visitors' && (
            <WebTrackingVisitors
              visitors={visitors}
              loading={visitorsLoading}
              onFetchVisitors={fetchVisitors}
              onSelectVisitor={handleSelectVisitor}
            />
          )}

          {activeTab === 'sites' && (
            <TrackedSiteSetup
              sites={sites}
              onAddSite={handleAddSite}
              onToggleActive={handleToggleActive}
              onRemoveSite={handleRemoveSite}
              onManageUsers={setManagingUsersForSiteId}
              canManage={canManage}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* User Management Modal */}
      {managingUsersForSiteId && managedSite && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <TrackedSiteUserManagement
            siteId={managingUsersForSiteId}
            siteName={managedSite.site_name}
            currentUserEmail={selectedSite?.owner_email}
            onClose={() => setManagingUsersForSiteId(null)}
            fetchSiteUsers={fetchSiteUsers}
            addSiteUser={addSiteUser}
            removeSiteUser={removeSiteUser}
          />
        </div>
      )}
    </div>
  );
};

export default WebTracking;
